import { axiosClient } from "auth-sdk";
import { addError } from "@wayflyer/blackbox-fe";
import type { ConnectionSchemas, CustomerwebSchemas } from "api-types";
import { useEffect } from "react";
import { useSearchParams } from "react-router";
import { useCustomerAppConfig } from "undercarriage";

import { useConnectionFlowsConfig } from "../config";

/**
 * After an OAuth redirect, surface any connection error via the `oauth-error`
 * search param and cancel the unconfirmed connection so it doesn't hang.
 */
export const useCancelConnectionOnError = (
  connectorSlug: string,
  completeConnectionProps: ConnectionSchemas.CompleteConnectionPropsSchema | undefined,
  isLoading: boolean
) => {
  const [, setSearchParams] = useSearchParams();
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const { cancelConnectionSubmitApiUrl } = useConnectionFlowsConfig();

  useEffect(() => {
    if (!isLoading && completeConnectionProps) {
      // Reconnects reusing an existing connection have no `accounts` field - that's not a failure.
      const hasAccounts = Object.keys(completeConnectionProps.accounts ?? {}).length > 0;
      const noAccountsError = completeConnectionProps.firstConnection && !hasAccounts ? "no_accounts" : null;
      const errorParam = completeConnectionProps.oAuthError ?? noAccountsError;
      if (errorParam) {
        setSearchParams({ platform: connectorSlug, "oauth-error": errorParam });
        // Cancel connection if there is an error to prevent hanging unconfirmed
        axiosClient
          .post<CustomerwebSchemas.CompleteCancelEmptySchema>(
            cancelConnectionSubmitApiUrl.replace(":customerSlug", customerDomain),
            completeConnectionProps
          )
          .catch((error: Error) => addError(error));
      }
    }
  }, [
    completeConnectionProps,
    isLoading,
    connectorSlug,
    setSearchParams,
    cancelConnectionSubmitApiUrl,
    customerDomain,
  ]);
};
